import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useCreateSupportManual, useUpdateSupportManual } from '@/hooks/use-support-manuals';
import { useSystemIds } from '@/hooks/use-system-ids';
import { useEffect } from 'react';

const manualSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório').max(200, 'Título muito longo'),
  content: z.string().min(50, 'Conteúdo deve ter pelo menos 50 caracteres'),
  category: z.string().min(1, 'Categoria é obrigatória'),
  system_id: z.string().optional(),
  is_published: z.boolean(),
});

type ManualFormData = z.infer<typeof manualSchema>;

interface ManualModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  manual?: any;
}

const MANUAL_CATEGORIES = [
  'Configuração',
  'Operação',
  'Manutenção',
  'Pneus',
  'Relatórios',
  'Integração',
  'Troubleshooting',
  'Geral',
];

export function ManualModal({ open, onOpenChange, manual }: ManualModalProps) {
  const { data: systemIds } = useSystemIds();
  const createManual = useCreateSupportManual();
  const updateManual = useUpdateSupportManual(manual?.id || '');

  const form = useForm<ManualFormData>({
    resolver: zodResolver(manualSchema),
    defaultValues: {
      title: '',
      content: '',
      category: 'Geral',
      is_published: false,
    },
  });

  useEffect(() => {
    if (manual) {
      form.reset({
        title: manual.title,
        content: manual.content,
        category: manual.category,
        system_id: manual.system_id || undefined,
        is_published: manual.is_published,
      });
    } else {
      form.reset({
        title: '',
        content: '',
        category: 'Geral',
        is_published: false,
      });
    }
  }, [manual, form]);

  const onSubmit = async (data: ManualFormData) => {
    try {
      if (manual) {
        await updateManual.mutateAsync({
          title: data.title,
          content: data.content,
          category: data.category,
          system_id: data.system_id,
          is_published: data.is_published,
        });
      } else {
        await createManual.mutateAsync({
          title: data.title,
          content: data.content,
          category: data.category,
          system_id: data.system_id,
          is_published: data.is_published,
        });
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving manual:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{manual ? 'Editar Manual' : 'Criar Manual'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Manual de Gestão de Pneus - FleetONE" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma categoria" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {MANUAL_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="system_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sistema (opcional)</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Geral (todos os sistemas)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="">Geral (todos os sistemas)</SelectItem>
                        {systemIds?.all?.map((system) => (
                          <SelectItem key={system.id} value={system.id}>
                            {system.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Conteúdo</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Digite o conteúdo do manual. Você pode usar HTML básico para formatação."
                      className="min-h-[400px] font-mono text-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_published"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Publicar Manual</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Manual publicado ficará visível para todos os usuários
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={createManual.isPending || updateManual.isPending}
              >
                {createManual.isPending || updateManual.isPending 
                  ? 'Salvando...' 
                  : manual ? 'Atualizar' : 'Criar Manual'
                }
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
